import {useEffect} from 'react';
import {QueryStatus} from '@reduxjs/toolkit/query';
import {type FCWithChildren} from 'types';
import {useLocalization} from 'localization';
import {
  isMockWorkId,
  metadataEditLoaded,
  selectMetadataEditSavedMetadataSelector,
  useGetChangeRequestsQuery,
  useGetWorkQuery,
  useMetadataSchemas,
  useMetadataSchemasCodelists,
} from 'services/metadata';
import {useLoadError} from 'services/utils';
import {useMockContext} from 'services/utils/contexts';
import {queryStatusToLoadStatus} from 'store/api';
import {loadMockWork} from 'store/api/mock/loadMock';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {Scene} from 'components/scene';

type Props = {
  workId: string;
};

export const MetadataEditStateGate: FCWithChildren<Props> = ({
  workId,
  children,
}) => {
  const {t} = useLocalization();
  const dispatch = useAppDispatch();

  const metadataSchemas = useMetadataSchemas();
  const metadataCodelists = useMetadataSchemasCodelists();

  const mock = useMockContext() || isMockWorkId(workId);

  const loadedMetadata = useAppSelector(
    selectMetadataEditSavedMetadataSelector,
  );

  const {data: workMetadataData, status: workMetadataStatus} = useGetWorkQuery(
    {id: workId},
    {
      skip: mock,
    },
  );

  const {data: changeRequestsData, status: changeRequestStatus} =
    useGetChangeRequestsQuery(
      {
        workId: workId,
        eans:
          ((workMetadataData?.manifestations ?? [])
            .map(m => m.ean)
            .filter(e => e) as string[]) ?? [],
      },
      {
        skip: mock || workMetadataStatus !== QueryStatus.fulfilled,
      },
    );

  useLoadError(
    queryStatusToLoadStatus(changeRequestStatus),
    'page.metadata.cr.load.failed',
    'warning',
  );

  useEffect(() => {
    if (workMetadataData && changeRequestsData) {
      if (
        !loadedMetadata ||
        loadedMetadata.work.id !== workMetadataData.work.id
      )
        dispatch(
          metadataEditLoaded({
            codelists: metadataCodelists,
            metadata: {
              ...workMetadataData,
              changeRequests: changeRequestsData,
            },
          }),
        );
    } else if (mock) {
      loadMockWork(workId).then(mockWork => {
        dispatch(
          metadataEditLoaded({
            codelists: metadataCodelists,
            metadata: {
              ...mockWork,
              changeRequests: [],
            },
          }),
        );
      });
    }
  }, [
    changeRequestsData,
    dispatch,
    loadedMetadata,
    metadataCodelists,
    metadataSchemas,
    mock,
    workId,
    workMetadataData,
  ]);

  return !loadedMetadata || loadedMetadata.work.id !== workId ? (
    <Scene>
      <Scene.Header title="" />
      <Scene.Content
        loadStatus={queryStatusToLoadStatus(workMetadataStatus)}
        error={
          workMetadataStatus === QueryStatus.rejected
            ? t('error.work.failedToLoad')
            : undefined
        }
        loadingType={t('general.entity.work')}
      />
    </Scene>
  ) : (
    <>{children}</>
  );
};
